import { useState } from 'react';
import { CheckCircle, Package, Grid, DollarSign, ShoppingCart, RefreshCw, AlertTriangle } from 'lucide-react';
import ConvertToSaleModal from './ConvertToSaleModal';
import ShareOptionsToolbar from './ShareOptionsToolbar';
import AddressedPersonModal from './AddressedPersonModal';

const CalculationResults = ({ result, onNewCalculation, onConversionComplete }) => {
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [addressedModal, setAddressedModal] = useState({ open: false, actionType: '', callback: null });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handlePrint = (addressedTo = '') => {
    const printWindow = window.open('', '_blank');
    const headerTitle = addressedTo ? `TILE CALCULATION for ${addressedTo}` : 'Tile Calculation Report';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tile Calculation - ${result.calculation_number}</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
              color: #333;
            }
            .print-header {
              text-align: center;
              border-bottom: 2px solid #D4AF37;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .print-header h1 {
              color: #1a1a2e;
              margin: 0 0 10px 0;
            }
            .print-header .calc-number {
              color: #D4AF37;
              font-size: 1.2rem;
              font-weight: bold;
            }
            .section {
              margin-bottom: 25px;
            }
            .section h3 {
              color: #1a1a2e;
              border-bottom: 1px solid #ddd;
              padding-bottom: 8px;
              margin-bottom: 15px;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }
            .info-item {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px dotted #eee;
            }
            .info-label { color: #666; }
            .info-value { font-weight: bold; color: #1a1a2e; }
            .highlight-box {
              background: #f8f8f8;
              border: 2px solid #D4AF37;
              border-radius: 8px;
              padding: 20px;
              text-align: center;
              margin: 20px 0;
            }
            .highlight-box .main-value {
              font-size: 2.5rem;
              font-weight: bold;
              color: #D4AF37;
            }
            .highlight-box .main-label {
              font-size: 1rem;
              color: #666;
            }
            .cost-box {
              background: #1a1a2e;
              color: #fff;
              border-radius: 8px;
              padding: 20px;
              text-align: center;
              margin: 20px 0;
            }
            .cost-box .cost-value {
              font-size: 2rem;
              font-weight: bold;
              color: #D4AF37;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              text-align: center;
              color: #666;
              font-size: 0.9rem;
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>${headerTitle}</h1>
            ${result.title ? `<h2 style="color: #1a1a2e; margin: 5px 0; font-size: 1.4rem;">${result.title}</h2>` : ''}
            <div class="calc-number">${result.calculation_number}</div>
            <div style="color: #666; margin-top: 10px;">
              ${new Date().toLocaleDateString('en-NG', { 
                year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
              })}
            </div>
          </div>

          <div class="section">
            <h3>Product Details</h3>
            <div class="info-grid">
              <div class="info-item"><span class="info-label">Product Code:</span><span class="info-value">${result.product.code}</span></div>
              <div class="info-item"><span class="info-label">Product Name:</span><span class="info-value">${result.product.name}</span></div>
              <div class="info-item"><span class="info-label">Tile Size:</span><span class="info-value">${result.product.tile_size}</span></div>
              <div class="info-item"><span class="info-label">Pieces per Carton:</span><span class="info-value">${result.product.pieces_per_carton}</span></div>
              <div class="info-item"><span class="info-label">Coverage per Carton:</span><span class="info-value">${result.product.coverage_per_carton_sqm} m²</span></div>
              <div class="info-item"><span class="info-label">Price per Carton:</span><span class="info-value">${formatCurrency(result.product.price_per_carton)}</span></div>
            </div>
          </div>

          <div class="section">
            <h3>Area Information</h3>
            <div class="info-grid">
              <div class="info-item"><span class="info-label">Length:</span><span class="info-value">${result.area_length_m} meters</span></div>
              <div class="info-item"><span class="info-label">Width:</span><span class="info-value">${result.area_width_m} meters</span></div>
              <div class="info-item"><span class="info-label">Total Area:</span><span class="info-value">${result.total_area_sqm} m²</span></div>
              <div class="info-item"><span class="info-label">Wastage Applied:</span><span class="info-value">${result.wastage_percent}%</span></div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="main-value">${result.cartons_needed}</div>
            <div class="main-label">Cartons Needed</div>
          </div>

          <div class="section">
            <h3>Calculation Results</h3>
            <div class="info-grid">
              <div class="info-item"><span class="info-label">Tiles Needed:</span><span class="info-value">${result.tiles_needed}</span></div>
              <div class="info-item"><span class="info-label">Coverage Provided:</span><span class="info-value">${result.coverage_provided_sqm} m²</span></div>
              <div class="info-item"><span class="info-label">Excess Coverage:</span><span class="info-value">${result.excess_coverage_sqm} m²</span></div>
            </div>
          </div>

          <div class="cost-box">
            <div>Estimated Total Cost</div>
            <div class="cost-value">${formatCurrency(result.estimated_cost)}</div>
          </div>

          <div class="footer">
            <p>This is a computer-generated calculation report.</p>
            <p>Prices are subject to change. Valid for 7 days.</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleConversionComplete = () => {
    setShowConvertModal(false);
    onConversionComplete();
  };

  // Generate text for WhatsApp sharing
  const generateCalculationText = (addressedTo = '') => {
    const headerText = addressedTo ? `*TILE CALCULATION for ${addressedTo}*` : '*TILE CALCULATION*';
    return `${headerText}
----------------------------------
${result.title ? `📋 *${result.title}*\n` : ''}Reference: ${result.calculation_number}

📦 *Product Details*
Code: ${result.product.code}
Name: ${result.product.name}
Size: ${result.product.tile_size}
Pieces/Carton: ${result.product.pieces_per_carton}

📐 *Area Information*
Dimensions: ${result.area_length_m}m × ${result.area_width_m}m
Total Area: ${result.total_area_sqm} m²
Wastage: ${result.wastage_percent}%

📊 *Calculation Results*
✅ Tiles Needed: ${result.tiles_needed}
✅ Cartons to Buy: ${result.cartons_needed}
Coverage Provided: ${result.coverage_provided_sqm} m²

💰 *Cost Estimate*
Price/Carton: ${formatCurrency(result.product.price_per_carton)}
*Total: ${formatCurrency(result.estimated_cost)}*

----------------------------------
_Prices subject to change. Valid for 7 days._`;
  };

  // Handle share action - requires addressed person first
  const handleBeforeAction = (actionType) => {
    return new Promise((resolve) => {
      setAddressedModal({
        open: true,
        actionType,
        callback: (addressedTo) => {
          setAddressedModal({ open: false, actionType: '', callback: null });
          resolve(addressedTo);
        }
      });
    });
  };

  // Handle addressed person modal confirmation
  const handleAddressedConfirm = (addressedTo) => {
    if (addressedModal.callback) {
      addressedModal.callback(addressedTo);
    }
  };

  // Handle addressed person modal cancel
  const handleAddressedCancel = () => {
    if (addressedModal.callback) {
      addressedModal.callback(null);
    }
    setAddressedModal({ open: false, actionType: '', callback: null });
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6 pb-4 border-b border-secondary-lightGray">
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-green-600 text-sm">
            <CheckCircle className="w-4 h-4" />
            Calculation Complete
            </div>
            <span className="text-sm font-mono text-accent-gold">{result.calculation_number}</span>
        </div>
        {result.title && (
            <h2 className="text-xl font-bold text-primary-black mt-2">{result.title}</h2>
        )}
        <p className="text-xs text-gray-400 mt-1">
            Input: {result.area_length_m}m × {result.area_width_m}m • Wastage: {result.wastage_percent}%
        </p>
      </div>

      {/* Product Summary */}
      <div className="flex items-center gap-3 p-3 bg-secondary-offWhite rounded-lg mb-4">
        <span className="px-2 py-1 bg-accent-gold text-white text-xs font-bold rounded">
          {result.product.code}
        </span>
        <div>
          <h4 className="font-medium text-primary-black">{result.product.name}</h4>
          <p className="text-xs text-primary-slate">{result.product.brand} • {result.product.tile_size}</p>
        </div>
      </div>

      {/* Area Info */}
      <div className="p-3 bg-secondary-offWhite rounded-lg mb-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-primary-slate">Area Dimensions:</span>
          <span className="font-medium text-primary-black">{result.area_length_m}m × {result.area_width_m}m</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-primary-slate">Total Area:</span>
          <span className="font-bold text-accent-gold">{result.total_area_sqm} m²</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-primary-slate">Wastage Applied:</span>
          <span className="font-medium text-primary-black">{result.wastage_percent}%</span>
        </div>
      </div>

      {/* Main Results */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-4 bg-secondary-offWhite border border-secondary-lightGray rounded-lg text-center">
          <Grid className="w-6 h-6 text-primary-slate mx-auto mb-2" />
          <p className="text-3xl font-bold text-primary-black">{result.tiles_needed}</p>
          <p className="text-sm text-primary-slate">Tiles Needed</p>
        </div>
        <div className="p-4 bg-accent-gold/10 border border-accent-gold/30 rounded-lg text-center">
          <Package className="w-6 h-6 text-accent-gold mx-auto mb-2" />
          <p className="text-3xl font-bold text-accent-gold">{result.cartons_needed}</p>
          <p className="text-sm text-primary-slate">Cartons to Buy</p>
        </div>
      </div>

      {/* Coverage Info */}
      <div className="p-3 bg-secondary-offWhite rounded-lg mb-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-primary-slate">Coverage Provided:</span>
          <span className="font-medium text-primary-black">{result.coverage_provided_sqm} m²</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-primary-slate">Excess Coverage:</span>
          <span className="font-medium text-green-600">+{result.excess_coverage_sqm} m²</span>
        </div>
      </div>

      {/* Cost Estimation */}
      <div className="p-4 bg-primary-black rounded-lg mb-4">
        <div className="flex justify-between text-sm text-gray-300 mb-2">
          <span>Price per Carton:</span>
          <span>{formatCurrency(result.price_per_carton)}</span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-gray-700">
          <span className="flex items-center gap-2 font-semibold text-white">
            <DollarSign className="w-4 h-4" /> Estimated Total:
          </span>
          <span className="text-2xl font-bold text-accent-gold">{formatCurrency(result.estimated_cost)}</span>
        </div>
      </div>

      {/* Stock Warning */}
      {!result.sufficient_stock && (
        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-red-600">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Insufficient Stock</p>
            <p className="text-sm">Only {result.product.stock_quantity} cartons available. Need {result.stock_shortage} more.</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <button 
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-accent-gold text-white rounded-lg font-semibold hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setShowConvertModal(true)}
            disabled={!result.sufficient_stock}
          >
            <ShoppingCart className="w-4 h-4" />
            Convert to Sale
          </button>
          <button 
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border border-secondary-lightGray text-primary-slate rounded-lg font-medium hover:bg-secondary-offWhite transition-colors"
            onClick={onNewCalculation}
          >
            <RefreshCw className="w-4 h-4" />
            New Calculation
          </button>
        </div>
        
        {/* Share Options - requires addressed person */}
        <ShareOptionsToolbar
          onPrint={handlePrint}
          onWhatsApp={(addressedTo) => {
            const text = generateCalculationText(addressedTo);
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
          }}
          generateText={generateCalculationText}
          onBeforeAction={handleBeforeAction}
          documentTitle={result.title || result.calculation_number}
        />
      </div>

      {/* Addressed Person Modal */}
      {addressedModal.open && (
        <AddressedPersonModal
          isOpen={addressedModal.open}
          onClose={handleAddressedCancel}
          onConfirm={handleAddressedConfirm}
          actionType={addressedModal.actionType}
          documentType="calculation"
        />
      )}

      {/* Convert Modal */}
      {showConvertModal && (
        <ConvertToSaleModal
          calculation={result}
          onClose={() => setShowConvertModal(false)}
          onSuccess={handleConversionComplete}
        />
      )}
    </div>
  );
};

export default CalculationResults;
